// Translation
import i18n from "i18next";
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { initReactI18next, useTranslation } from "react-i18next";
// React
import { useEffect, useState } from "react";
// React Router dom
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
// dayjs
import dayjs from 'dayjs';
// Protected route
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
// Pages
import Error from "./pages/Error/Error";
import InProgress from "./pages/InProgress/InProgress";
import TestPlanReports from "./pages/TestPlanReports/TestPlanReports";
import TestPlans from "./pages/TestPlans/TestPlans";
import Exchanges from "./pages/exchanges/Exchanges";
import Home from "./pages/home/Home";
import SystemRegistration from "./pages/systemRegistration/SystemRegistration";
import Systems from "./pages/systems/Systems";
import SystemsSelection from "./pages/systemsSelection/SystemsSelection";
import TestPlanReport from "./pages/testPlanReport/TestPlanReport";
import TestPlanSystemsSelection from "./pages/testPlanSystemsSelection/TestPlanSystemsSelection";
import TestPlanViewer from "./pages/testPlanViewer/TestPlanViewer";
import TestReportDetails from "./pages/testReportDetails/TestReportDetails";
import TestReports from "./pages/testReports/TestReports";
import TestScripts from "./pages/testScripts/TestScripts";
import ValidationReport from "./pages/validationReport/ValidationReport";
import ValidationReports from "./pages/validationReports/ValidationReports";
import ValidatorHome from "./pages/validatorHome/ValidatorHome";
import relativeTime from 'dayjs/plugin/relativeTime';
import ExchangeDetails from "./pages/exchangeDetails/ExchangeDetails";
// import MyTestPlans from "./pages/myTestPlans/MyTestPlans";
import TestScriptViewer from "./pages/testScriptViewer/TestScriptViewer";
import FixtureEdition from "./pages/FixtureEdition/FixtureEdition";
import EvalutionReport from "./pages/evaluationReport/EvaluationReport";
import EvalutionReports from "./pages/evaluationReports/EvaluationReports";
import PackageLoader from "./pages/packageLoader/packageLoader";
import TestEditor from "./pages/TestEditor/TestEditor";
import { isLightMode } from "./services/LightMode";
import AuditEventExchanges from "./pages/auditEventExchanges/auditEventExchanges"
import AuditEventExchangeDetails from "./pages/auditEventExchangeDetails/auditEventExchangeDetails"
import { toPublicUrl } from "./services/PublicUrl";
import { EnvironmentProvider  } from "./context/EnvironmentContext";
import { appInitializer } from "./services/AppInitializer";
import ImplementationGuidePage from "./pages/ImplementationGuide/ImplementationGuidePage";

////////////////////////////////////
//       dayjs/translation        //
////////////////////////////////////

require('dayjs/locale/fr');

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['fr', 'en'],
    backend: {
      loadPath: toPublicUrl("/locales/{{lng}}/{{ns}}.json"),
    },
  })

dayjs.extend(relativeTime);
dayjs.locale(i18n.language);

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;

  useTranslation();

  const [initialized, setInitialized] = useState(false);
  
  useEffect(() => {
    const init = async () => {
      await appInitializer(); 
      setInitialized(true);
    };

    init();
  }, []);
  
  /////////////////////////////////////
  //            LifeCycle            //
  /////////////////////////////////////

  /**
   * Set title and meta description
   */
  useEffect(() => {
    let title = "";
    let metaDescription = "";

    ///////////////////////////////////////////////
    //       Page title and metadescription      //
    ///////////////////////////////////////////////

    switch (pathname) {
      case "/":
      case "/Pandora/Home":
      case "/Home":
        title = "Home page";
        metaDescription = "";
        break;
      case "/Error":
        title = "Oops !";
        metaDescription = "";
        break;
      case "/InProgress":
        title = "Coming Soon";
        metaDescription = "";
        break;
      case "/PackageLoader":
        title = "Package Loader";
        metaDescription = "";
        break;
      case "/ValidatorHome":
        title = "Validator Home";
        metaDescription = "";
        break;
      case "/ValidationReport":
        title = "Validation report";
        metaDescription = "";
        break;
      case "/TestScript":
        title = "Test script";
        metaDescription = "";
        break;
      case "/SystemsSelection":
        title = "Systems Selection";
        metaDescription = "";
        break;
      case "/TestReportDetails":
        title = "Test report details";
        metaDescription = "";
        break;
      case "/SystemRegistration":
        title = "System registration";
        metaDescription = "";
        break;
      case "/SystemEdition":
        title = "System edition";
        metaDescription = "";
        break;
      case "/Systems":
        title = "Systems";
        metaDescription = "";
        break;
      case "/Exchanges":
        title = "Exchanges";
        metaDescription = "";
        break;
      case "/SignIn":
        title = "SignIn";
        metaDescription = "";
        break;
      case "/TestPlans":
        title = "TestPlans";
        metaDescription = "";
        break;
      case "/TestPlanDetails":
        title = "TestPlan viewer";
        metaDescription = "";
        break;
      case "/TestPlan/SystemSelection/:testPlanId":
        title = "TestPlan System Selection";
        metaDescription = "";
        break;
      case "/TestPlan/Report/:testPlanId":
        title = "TestPlan report";
        metaDescription = "";
        break;
      case "/TestPlan/Report":
        title = "TestPlan report list";
        metaDescription = "";
        break;
      case "/ExchangeDetails/:exchangeId":
        title = "Exchange details";
        metaDescription = "";
        break;
      case "/ValidationReports":
        title = "Validation reports";
        metaDescription = "";
        break;
      case "/TestScriptViewer/:testScriptId":
        title = "Test script viewer";
        metaDescription = "";
        break;
      case "EvaluationReport/:evaluationReportId":
        title = "Evaluation report";
        metaDescription = "";
        break;
      case "EvaluationReports":
        title = "Evaluation reports";
        metaDescription = "";
        break;
      case "TestEditor":
        title = "Test Editor";
        metaDescription = "";
        break;
      case "/ImplementationGuide":
      case "/Pandora/ImplementationGuide":
        title = `Implementation Guide`;
        metaDescription = "";
        break;
      // case "/MyTestPlans":
      //   title = "My Test Plans";
      //   metaDescription = "";
      //   break;
    }

    if (title) {
      document.title = title;
    }

    if (metaDescription) {
      const metaDescriptionTag: HTMLMetaElement | null = document.querySelector(
        'head > meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription;
      }
    }
  }, [pathname]);

  const PublicRouteElement = ({ children }: { children: React.ReactNode }) => (
    <ProtectedRoute needsLogin={false}>
      {children}
    </ProtectedRoute>
  );

  const PrivateRouteElement = ({ children }: { children: React.ReactNode }) => (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );

  if (!initialized) return null;

  /////////////////////////////////////
  //          Page Content           //
  /////////////////////////////////////

  return (
    <EnvironmentProvider>
      <Routes>
        <Route index element={  
          <PublicRouteElement>
            <Home/>
          </PublicRouteElement>
        }/>
        <Route
          path="/"
          element={
          <PublicRouteElement>
            <Home/>
          </PublicRouteElement>}
        />
        <Route
          path="/Home"
          element={
          <PublicRouteElement>
            <Home/>
          </PublicRouteElement>}
        />
        <Route
          path="Pandora/Home"
          element={
          <PublicRouteElement>
            <Home/>
          </PublicRouteElement>}
        />
        <Route
          path="/Error"
          element={
          <PublicRouteElement>
            <Error/>
          </PublicRouteElement>}
        />
        <Route
          path="/InProgress"
          element={
          <PublicRouteElement>
            <InProgress />
          </PublicRouteElement>}
        />
        <Route
          path="/ValidatorHome/:validatorParam"
          element={
            <PublicRouteElement>
              <ValidatorHome />
            </PublicRouteElement>
          }
        />
        <Route
          path="/PackageLoader/:validatorParam"
          element={
            <PublicRouteElement>
              <PackageLoader />
            </PublicRouteElement>
          }
        />
        <Route
          path="/ValidationReport/:validationReportId"
          element={
          <PublicRouteElement>
            <ValidationReport />
          </PublicRouteElement>}
        />
        {/** Private Route */}
        <Route
          path="/TestScript"
          element={
            <PrivateRouteElement>
              <TestScripts />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/SystemsSelection/:testScriptId"
          element={
            <PrivateRouteElement>
              <SystemsSelection />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlan/SystemSelection/:testPlanId"
          element={
            <PrivateRouteElement>
              <TestPlanSystemsSelection />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestReports"
          element={
            <PrivateRouteElement>
              <TestReports />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestReportDetails/:testReportId"
          element={
            <PrivateRouteElement>
              <TestReportDetails />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/SystemRegistration"
          element={
            <PrivateRouteElement>
              <SystemRegistration />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/SystemEdition/:systemId"
          element={
            <PrivateRouteElement>
              <SystemRegistration />
            </PrivateRouteElement>
          }
        />
        {!isLightMode() && <Route
          path="/Systems"
          element={
            <PrivateRouteElement>
              <Systems />
            </PrivateRouteElement>
          }
        />}
        <Route
          path="/Exchanges"
          element={
            <PrivateRouteElement>
              <Exchanges />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlans"
          element={
            <PrivateRouteElement>
              <TestPlans />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlanDetails/:testPlanId"
          element={
            <PrivateRouteElement>
              <TestPlanViewer />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlan/SystemSelection/:testPlanId"
          element={
            <PrivateRouteElement>
              <TestPlanSystemsSelection />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlan/Report"
          element={
            <PrivateRouteElement>
              <TestPlanReports />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestPlan/Report/:testPlanId"
          element={
            <PrivateRouteElement>
              <TestPlanReport />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/ExchangeDetails/:exchangeId"
          element={
            <PrivateRouteElement>
              <ExchangeDetails />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/ValidationReports"
          element={
            <PrivateRouteElement>
              <ValidationReports />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestScriptViewer/:testScriptId"
          element={
            <PrivateRouteElement>
              <TestScriptViewer />
            </PrivateRouteElement>
          }
        />
        {/* <Route
          path="/MyTestPlans"
          element={<MyTestPlans />}
        /> */}
        <Route
          path="/FixtureEdition/:fixtureId"
          element={
            <PrivateRouteElement>
              <FixtureEdition />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/EvaluationReport/:evaluationReportId"
          element={
            <PrivateRouteElement>
              <EvalutionReport />
            </PrivateRouteElement>}
        />
        <Route
          path="/EvaluationReports"
          element={
            <PrivateRouteElement>
              <EvalutionReports />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestEditor"
          element={
            <PrivateRouteElement>
              <TestEditor />
            </PrivateRouteElement>
          }
        />
        <Route
          path="/TestEditor/:testId"
          element={
            <PrivateRouteElement>
              <TestEditor />
            </PrivateRouteElement>
          }
        />
        {isLightMode() && (
        <Route 
          path="/AuditEventExchanges" 
          element={
            <PrivateRouteElement>
              <AuditEventExchanges />
            </PrivateRouteElement>
            } 
        />
        )}
        {isLightMode() && (
        <Route 
          path="/AuditEventExchangeDetails/:exchangeId" 
          element={
            <PrivateRouteElement>
              <AuditEventExchangeDetails />
            </PrivateRouteElement>
          } 
        />
        )}
        <Route path="*" element={<Navigate to="/Home" replace />} />
        <Route
        path="/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      <Route
        path="/Pandora/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      </Routes>
    </EnvironmentProvider>
  );
}
export default App;
